import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, GripVertical, Building2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loading } from "@/components/common/Loading";
import { EnterpriseIcon, ENTERPRISE_ICON_NAMES } from "@/components/enterprise/enterpriseIcons";
import { SandboxedEmbed, EMBED_TRUST_WARNING } from "@/components/common/SandboxedEmbed";

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    {label && <Label className="text-sm">{label}</Label>}
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

function StringList({ items, onChange, placeholder, testId }) {
  const list = items || [];
  const set = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  return (
    <div className="space-y-2" data-testid={testId}>
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground/50" />
          <Input value={v} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} data-testid={`${testId}-input-${i}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-destructive" onClick={() => onChange(list.filter((_, idx) => idx !== i))} data-testid={`${testId}-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="secondary" onClick={() => onChange([...list, ""])} data-testid={`${testId}-add`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
    </div>
  );
}

export default function EnterpriseEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/enterprise").then(({ data }) => setC(data)).catch(() => toast.error("Could not load enterprise content."));
  }, []);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const setOff = (i, patch) => set("offerings", c.offerings.map((o, idx) => (idx === i ? { ...o, ...patch } : o)));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/enterprise", c);
      setC(data);
      toast.success("Enterprise page saved — live on /enterprise.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  if (!c) return <Loading />;

  return (
    <div data-testid="enterprise-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-[680] text-foreground"><Building2 className="h-5 w-5 text-primary" /> Enterprise page content</h2>
          <p className="text-sm text-muted-foreground">Edit every section of <span className="font-[560]">/enterprise</span> (the Teams &ldquo;Contact us&rdquo; page) — changes go live instantly, no redeploy.</p>
        </div>
        <a href="/enterprise" target="_blank" rel="noreferrer"><Button variant="secondary" data-testid="enterprise-view-live">View live page</Button></a>
      </div>

      <Accordion type="multiple" defaultValue={["hero", "form"]} className="mt-6 space-y-3">
        <AccordionItem value="hero" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-hero">Hero section</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Headline"><Input value={c.hero_headline || ""} onChange={(e) => set("hero_headline", e.target.value)} data-testid="enterprise-hero-headline-input" /></F>
            <F label="Subheadline"><Textarea rows={3} value={c.hero_subheadline || ""} onChange={(e) => set("hero_subheadline", e.target.value)} data-testid="enterprise-hero-subheadline-input" /></F>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <F label="Button label"><Input value={c.hero_cta_label || ""} onChange={(e) => set("hero_cta_label", e.target.value)} /></F>
              <F label="Button target" hint="#enterprise-form scrolls to the form, or a URL"><Input value={c.hero_cta_target || ""} onChange={(e) => set("hero_cta_target", e.target.value)} /></F>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="intro" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-intro">Intro</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.intro_heading || ""} onChange={(e) => set("intro_heading", e.target.value)} /></F>
            <F label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={5} value={c.intro_body || ""} onChange={(e) => set("intro_body", e.target.value)} /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="offerings" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-offerings">What Teams plans include</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.offerings_heading || ""} onChange={(e) => set("offerings_heading", e.target.value)} /></F>
            <div className="space-y-3">
              {(c.offerings || []).map((o, i) => (
                <Card key={i} className="space-y-3 p-4" data-testid={`enterprise-offering-editor-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-[640] text-muted-foreground">Item {i + 1}</span>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => set("offerings", c.offerings.filter((_, idx) => idx !== i))} data-testid={`enterprise-offering-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-2"><Input value={o.title || ""} onChange={(e) => setOff(i, { title: e.target.value })} placeholder="Title" data-testid={`enterprise-offering-title-${i}`} /></div>
                    <Select value={o.icon || "Users"} onValueChange={(v) => setOff(i, { icon: v })}>
                      <SelectTrigger data-testid={`enterprise-offering-icon-${i}`}><span className="flex items-center gap-2"><EnterpriseIcon name={o.icon} className="h-4 w-4" /><SelectValue /></span></SelectTrigger>
                      <SelectContent className="max-h-72">{ENTERPRISE_ICON_NAMES.map((n) => <SelectItem key={n} value={n}><span className="flex items-center gap-2"><EnterpriseIcon name={n} className="h-4 w-4" />{n}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Textarea rows={2} value={o.body || ""} onChange={(e) => setOff(i, { body: e.target.value })} placeholder="Description" data-testid={`enterprise-offering-body-${i}`} />
                </Card>
              ))}
              <Button size="sm" variant="secondary" onClick={() => set("offerings", [...(c.offerings || []), { title: "", body: "", icon: "Users" }])} data-testid="enterprise-offering-add"><Plus className="mr-1 h-3.5 w-3.5" /> Add item</Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="benefits" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-benefits">Why teams choose us</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.benefits_heading || ""} onChange={(e) => set("benefits_heading", e.target.value)} /></F>
            <F label="Benefits"><StringList items={c.benefits} onChange={(v) => set("benefits", v)} placeholder="Benefit" testId="enterprise-benefits-list" /></F>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="form" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-form">Contact form embed</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.form_heading || ""} onChange={(e) => set("form_heading", e.target.value)} /></F>
            <F label="Intro"><Textarea rows={3} value={c.form_intro || ""} onChange={(e) => set("form_intro", e.target.value)} /></F>
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="enterprise-embed-warning">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{EMBED_TRUST_WARNING}</span>
            </div>
            <F label="Form embed code (HTML)" hint="Paste your Cognito Forms (or other) embed code — seamless/script or iframe. It renders inside a sandboxed frame on /enterprise with no redeploy. Leave blank to show a fallback email.">
              <Textarea rows={8} value={c.form_embed_html || ""} onChange={(e) => set("form_embed_html", e.target.value)} className="font-mono text-xs" placeholder="<!-- Paste your Cognito Forms embed code here -->" data-testid="enterprise-form-embed-input" />
            </F>
            {c.form_embed_html && c.form_embed_html.trim() ? (
              <div className="space-y-1.5" data-testid="enterprise-embed-preview">
                <Label className="text-sm">Preview (sandboxed)</Label>
                <div className="rounded-md border border-border bg-background p-2">
                  <SandboxedEmbed html={c.form_embed_html} title="Enterprise form preview" minHeight={420} />
                </div>
              </div>
            ) : null}
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="final" className="rounded-none border border-border px-4">
          <AccordionTrigger data-testid="enterprise-acc-final">Final call-to-action</AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            <F label="Heading"><Input value={c.final_heading || ""} onChange={(e) => set("final_heading", e.target.value)} /></F>
            <F label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={4} value={c.final_body || ""} onChange={(e) => set("final_body", e.target.value)} /></F>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <F label="Button label"><Input value={c.final_cta_label || ""} onChange={(e) => set("final_cta_label", e.target.value)} /></F>
              <F label="Button target"><Input value={c.final_cta_target || ""} onChange={(e) => set("final_cta_target", e.target.value)} /></F>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button size="lg" onClick={save} disabled={saving} className="shadow-lg" data-testid="enterprise-save-button"><Save className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}
